import { FC, Fragment, ReactNode, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { isOk, isPending, Result } from "@/common/common";
import { Box, Card, Grid, VBox } from "@/components-v2/Box";
import { LocalizedText } from "@/common/common";
import { TMessageKey } from "@/i18n/types.gen";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { orderlessFilter } from "@/utils/filter";
import { Tabs } from "@/components-v2/Tabs";
import { MDateInput } from "@/components-v2/DateInput";
import { Skeleton } from "@/components-v2/Skeleton";
import { FieldValue, SystemFieldValue } from "@/customFields/customFieldsComponents";
import { CompanyCustomField, systemFieldLabels } from "@/customFields/customFieldsDomain";
import { companyRoleLabel } from "@/utils/Roles";
import { personFieldLabel, textFor } from "./dashboardDialogsDomain";
import { useDispatch, useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { DashboardCardConfig, DashboardCardData } from "./dashboardApiClient";
import {
  cardSkeleton,
  type CardSkeletonShape,
  loadPreviewData,
  MAP_CARD_HEIGHT,
  PreviewEntry,
  previewCompany,
  previewEntriesKey,
  previewGroup,
  previewNote,
  setPreviewCompany,
  setPreviewGroup,
} from "./dashboardPreviewDomain";
import {
  chosenDate,
  commitAsOfDate,
  dashboardGroupLabel,
  DashboardSettingsState,
} from "./dashboardSettingsDomain";
import { Alert } from "@/components-v2/Alert";
import { DataGraph } from "@/components-v2/DataGraph/DataGraph";
import { TemplateContent, TemplateType } from "@/templates/templateDomain";
import { mapTemplateGraphProps } from "@/templates/templatePanelDomain";

export const usePreviewData = (
  state: DashboardSettingsState,
  entries: PreviewEntry[],
  hasDatePicker: boolean,
  savedVisibleTo?: string[],
) => {
  const dispatch = useDispatch();
  const accountId = state.accountId;
  const company = previewCompany(state);
  const asOf = chosenDate(hasDatePicker, state.previewAsOf);
  const previewAs = previewGroup(state, savedVisibleTo)?.grantee;
  const entriesKey = previewEntriesKey(entries);
  useEffect(() => {
    if (previewAs) {
      dispatch(loadPreviewData(accountId, company, JSON.parse(entriesKey), { asOf, previewAs }));
    }
  }, [dispatch, accountId, company, entriesKey, asOf, previewAs]);
};

export const PreviewAsSelect: FC<{ state: DashboardSettingsState; savedVisibleTo?: string[] }> =
  observer(({ state, savedVisibleTo }) => {
    const { t } = useTranslation();
    return (
      <Box gap={"sm"} alignItems={"center"} wrap>
        <Box fg={"subtle"} whitespace={"nowrap"}>
          {t("enterprise-account.dashboards.preview-as")}
        </Box>
        <PreviewGroupSelect state={state} savedVisibleTo={savedVisibleTo} />
        <PreviewCompanySelect state={state} />
      </Box>
    );
  });

const PreviewGroupSelect: FC<{ state: DashboardSettingsState; savedVisibleTo?: string[] }> =
  observer(({ state, savedVisibleTo }) => {
    const { tf } = useTranslation();
    const dispatch = useDispatch();
    const selected = previewGroup(state, savedVisibleTo);
    const groups = state.overview?.groups ?? [];
    return groups.length === 0 ? null : (
      <DropdownMenu
        button={{ type: "select", label: selected && tf(dashboardGroupLabel(selected)) }}
        alignment={"end"}
        items={groups.map((group) => ({
          type: "radio-item",
          label: dashboardGroupLabel(group),
          isSelected: group.grantee === selected?.grantee,
          onSelect: () => dispatch(setPreviewGroup(state, group.grantee)),
        }))}
      />
    );
  });

const PreviewCompanySelect: FC<{ state: DashboardSettingsState }> = observer(({ state }) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("");
  const selected = previewCompany(state);
  return state.companies.length === 0 ? null : (
    <DropdownMenu
      button={{ type: "select", label: selected?.name }}
      alignment={"end"}
      filter={filter}
      filterPlaceholder={{ key: "search" }}
      onFilterChange={setFilter}
      items={orderlessFilter(
        state.companies,
        (company) => [company.name, company.orgNo],
        filter,
      ).map((company) => ({
        type: "radio-item",
        label: company.name,
        isSelected: company.orgNo === state.previewOrgNo,
        onSelect: () => {
          setFilter("");
          dispatch(setPreviewCompany(state, company.orgNo));
        },
      }))}
    />
  );
});

export const PreviewVisibilityNote: FC<{
  state: DashboardSettingsState;
  savedVisibleTo?: string[];
  visibleTo: string[];
  isEnabled: boolean;
}> = observer(({ state, savedVisibleTo, visibleTo, isEnabled }) => {
  const message = previewNote(previewGroup(state, savedVisibleTo), visibleTo, isEnabled);
  return message ? <Alert type={"info"} message={message} /> : null;
});

export const PreviewTabBar: FC<{ title?: ReactNode; end?: ReactNode; inset?: boolean }> = ({
  title,
  end,
  inset,
}) => (
  <Box
    justifyContent={"space-between"}
    alignItems={"center"}
    gap={"md"}
    paddingX={inset ? "lg" : undefined}
    borderBottom
    wrap
  >
    {title ? (
      <Tabs tabs={[{ id: "preview", label: title, isActive: true, onSelect: () => undefined }]} />
    ) : (
      <span />
    )}
    {end && <Box paddingY={"sm"}>{end}</Box>}
  </Box>
);

export const PreviewHeadingBar: FC<{
  state: DashboardSettingsState;
  dateInputId: string;
  heading?: string;
  hasDatePicker: boolean;
}> = observer(({ state, dateInputId, heading, hasDatePicker }) => {
  const { t } = useTranslation();
  return heading || hasDatePicker ? (
    <Box justifyContent={"space-between"} alignItems={"center"} gap={"md"} wrap>
      {heading ? <h3>{heading}</h3> : <span />}
      {hasDatePicker && (
        <Box flex={"none"}>
          <MDateInput
            id={dateInputId}
            model={state.previewAsOf}
            placeholder={t("enterprise-account.dashboards.as-of-today")}
            onChange={(params) => commitAsOfDate(state.previewAsOf, params.input)}
          />
        </Box>
      )}
    </Box>
  ) : null;
});

export const CardDataView: FC<{
  result?: Result<DashboardCardData>;
  config: DashboardCardConfig;
}> = observer(({ result, config }) => {
  const { t } = useTranslation();
  if (!result) {
    return <Box fg={"subtle"}>{t("enterprise-account.dashboards.card-placeholder")}</Box>;
  }
  if (isPending(result)) {
    return <CardSkeleton shape={cardSkeleton(config)} />;
  }
  if (!isOk(result)) {
    return <Box fg={"error"}>{t("enterprise-account.dashboards.card-data-failed")}</Box>;
  }
  const data = result.data;
  switch (data.type) {
    case "ENTITY_DATA":
      return data.rows.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-fields")}</Box>
      ) : (
        <PropertyGrid
          rows={data.rows.map((row) =>
            row.source === "SYSTEM"
              ? {
                  key: row.field.systemField,
                  label: t(systemFieldLabels[row.field.systemField]),
                  value: <SystemFieldValue field={row.field} />,
                }
              : {
                  key: row.field.definitionId,
                  label: row.field.name,
                  value: <CustomValue field={row.field} />,
                },
          )}
        />
      );
    case "COMPANY_ROLES":
      return data.rows.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-holders")}</Box>
      ) : (
        <PropertyGrid
          rows={data.rows.map((row, index) => ({
            key: String(index),
            label: t(companyRoleLabel(row)),
            value: row.name,
          }))}
        />
      );
    case "ROLE_HOLDER":
      return data.holders.length === 0 ? (
        <Box fg={"subtle"}>{t("enterprise-account.dashboards.no-holders")}</Box>
      ) : (
        <VBox>
          {data.holders.map((holder, index) => (
            <PropertyGrid
              key={index}
              separated={index > 0}
              rows={holder.rows.map((row) =>
                row.source === "SYSTEM"
                  ? {
                      key: row.field,
                      label: t(personFieldLabel(row.field)),
                      value: <FieldValue value={row.value} />,
                    }
                  : {
                      key: row.field.definitionId,
                      label: row.field.name,
                      value: <CustomValue field={row.field} />,
                    },
              )}
            />
          ))}
        </VBox>
      );
    case "TEMPLATE":
      return <TemplateCardView content={data.content} />;
  }
});

const TemplateCardView: FC<{ content: TemplateContent }> = ({ content }) => {
  switch (content.type) {
    case TemplateType.PROJECT:
      return null;
    case TemplateType.MAP:
      return (
        <VBox overflow={"hidden"} htmlProps={{ style: { height: MAP_CARD_HEIGHT } }}>
          <DataGraph {...mapTemplateGraphProps(content)} />
        </VBox>
      );
  }
};

const CardSkeleton: FC<{ shape: CardSkeletonShape }> = ({ shape }) => {
  switch (shape.type) {
    case "rows":
      return (
        <Grid cols={2} gap={"sm"}>
          {Array.from({ length: shape.count }, (_, index) => (
            <Fragment key={index}>
              <Skeleton width={"2/3"} />
              <Skeleton width={"1/2"} />
            </Fragment>
          ))}
        </Grid>
      );
    case "block":
      return (
        <Box
          className={"shimmer-block rounded-sm"}
          htmlProps={{ style: { height: shape.height } }}
        />
      );
  }
};

type PropertyRow = { key: string; label: string; value: ReactNode };

const PropertyGrid: FC<{ rows: PropertyRow[]; separated?: boolean }> = ({ rows, separated }) => (
  <Grid
    cols={2}
    gap={"sm"}
    borderTop={separated}
    paddingTop={separated ? "md" : undefined}
    marginTop={separated ? "md" : undefined}
  >
    {rows.map((row) => (
      <Fragment key={row.key}>
        <Box fg={"subtle"}>{row.label}</Box>
        <Box className={"min-w-0 break-words"}>{row.value}</Box>
      </Fragment>
    ))}
  </Grid>
);

const CustomValue: FC<{ field: CompanyCustomField }> = ({ field }) => (
  <FieldValue
    value={field.value}
    selectOptions={field.selectOptions}
    numberFormat={field.numberFormat}
  />
);

export const PreviewText: FC<{ text?: string; placeholder: TMessageKey }> = ({
  text,
  placeholder,
}) => {
  const { t } = useTranslation();
  return text ? <>{text}</> : <Box fg={"subtle"}>{t(placeholder)}</Box>;
};

export const PreviewCard: FC<{
  state: DashboardSettingsState;
  heading: LocalizedText[];
  previewKey: string;
  config: DashboardCardConfig;
}> = observer(({ state, heading, previewKey, config }) => {
  const language = useLanguage();
  return (
    <Card
      title={
        <PreviewText
          text={textFor(heading, language)}
          placeholder={"enterprise-account.dashboards.card-heading"}
        />
      }
    >
      <CardDataView
        result={state.previewOrgNo ? state.previewData[previewKey] : undefined}
        config={config}
      />
    </Card>
  );
});
